/**
 * Clipboard flows of the timeline (W2-03 note-lane; DESIGN.md §5.4 "Copy and paste"; RPE SEL-04..07).
 *
 *  - Copy / cut (Ctrl+C / Ctrl+X): `Core.clipboard.copy` of the selected notes, keys and segments, kept by the
 *    clipboard service (also written to the system clipboard as `PHIEDIT-CLIP/1` text) and a toast "Copied 14 notes,
 *    6 keyframes (12:0/1 to 13:3/4)". Cut also deletes the notes and keys in one undoable step.
 *  - Paste (Ctrl+V): a GHOST appears with its earliest item at the mouse beat (or the playhead when the mouse is
 *    outside the timeline; setting "Paste anchor") and follows the mouse in time, snapped. Click or Enter commits;
 *    Esc cancels; M mirrors; Tab / Shift+Tab cycle the target line; [ / ] shift by one snap step. The setting "Paste
 *    immediately" (the RPE preset's default) skips the ghost. The ghost lives in the editor store's placement state
 *    (`kind: 'paste'`), so the motion columns (W2-04a) draw the key part of the same ghost.
 *  - Variants: Ctrl+B mirrored paste, Alt+V add values (keys added onto the target curve at the same beats; values
 *    without a matching key are reported with "Paste the rest as a new layer"), paste special (DIALOG.pasteSpecial,
 *    W2-11, returns the choice and this package pastes), Ctrl+D duplicate right after the selection's span.
 * Every paste is one transaction through `Core.clipboard.paste`; the pasted notes and keys are selected.
 */

import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { Json } from '../../core/util/types';
import type { ClipboardPayload, ClipTrackSlice, PasteMode } from '../../core/commands/types';
import type { KeyId, LineId, NoteId } from '../../core/model/types';
import { Core } from '../../core/commands/index';
import { continuityOf, jumpsMade } from '../../core/commands/keyValueRules';
import { DIALOG, type PasteSpecialResult } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';
import type { PlacementState } from '../../state/types';
import { pasteGhostNotes, type PasteGhost } from '../../render/timeline/ghostPaste';
import { m } from './noteLane.messages';
import {
  activeLineOf,
  beatText,
  commandKeysText,
  hostOf,
  isLockedLine,
  itemsText,
  laneSession,
  lineLabel,
  livePointer,
  lockedReason,
  playheadBeat,
  ref,
  selectedItems,
  snapBeatAt,
  snapStepBeat,
  tempoOfStore,
  gestureHints,
  type LaneSession,
  type PasteGhostChoice,
} from './laneSession';

export type PasteVariant = 'normal' | 'mirror' | 'addValues';

export const NORMAL_MODE: PasteMode = Object.freeze({
  mirror: false,
  addValues: false,
  asNewLayer: false,
  originalBeats: false,
  toOriginalLines: false,
  repeat: 1,
});

export function modeFor(variant: PasteVariant): PasteMode {
  return { ...NORMAL_MODE, mirror: variant === 'mirror', addValues: variant === 'addValues' };
}

/** Number of keys in a payload's track slices. */
export function payloadKeyCount(p: ClipboardPayload): number {
  let n = 0;
  for (const t of p.tracks) n += t.keys.length;
  return n;
}

// ---------------------------------------------------------------------------------------------------------------
// Copy / cut
// ---------------------------------------------------------------------------------------------------------------

/** Copies (and with `cut` deletes) the selection; returns the payload or null when nothing was copied. */
export async function copySelection(services: AppServices, cut: boolean): Promise<ClipboardPayload | null> {
  const store = services.store;
  if (store === null) return null;
  const sel = selectedItems(services);
  if (sel.notes.length + sel.keys.length + sel.segments.length === 0) {
    services.ui.toast(ref(m.toastNothingCopied), { kind: 'info' });
    return null;
  }
  // Cut deletes: refused as a whole on a locked line (Copy still works there).
  const locked = cut ? lockedReason(services, { notes: sel.notes, keys: [...sel.keys, ...sel.segments] }) : null;
  if (locked !== null) {
    services.ui.toast(locked, { kind: 'warning' });
    return null;
  }
  const payload = Core.clipboard.copy(
    { doc: store.doc, idx: store.idx, tempo: tempoOfStore(store) },
    { notes: sel.notes, keys: sel.keys, segments: sel.segments },
  );
  if (payload === null) {
    services.ui.toast(ref(m.toastNothingCopied), { kind: 'info' });
    return null;
  }
  await services.clipboard.set(payload);
  const items = itemsText(services, { notes: payload.notes.length, keys: payloadKeyCount(payload) });
  const from = beatText(services, payload.origin);
  const to = beatText(services, Beats.add(payload.origin, payload.span));
  if (!cut) {
    services.ui.toast(ref(m.toastCopied, { items, from, to }), { kind: 'info', durationMs: 3000 });
    return payload;
  }
  const keys = [...new Set<KeyId>([...sel.keys, ...sel.segments])];
  const out = store.transact({ label: ref(m.txCut, { items }), command: 'edit.cut' }, (tx) => {
    if (sel.notes.length > 0) Core.notes.deleteNotes(tx, { ids: sel.notes });
    if (keys.length > 0) Core.keys.deleteKeys(tx, { keys });
    tx.setSelection({ notes: [], keys: [], segments: [] });
  });
  if (!out.ok) {
    services.ui.toast(out.reason, { kind: 'error' });
    return payload;
  }
  services.ui.toast(ref(m.toastCut, { items, from, to }), { kind: 'info', undo: true, durationMs: 3500 });
  return payload;
}

// ---------------------------------------------------------------------------------------------------------------
// Anchors
// ---------------------------------------------------------------------------------------------------------------

/** Mouse beat over any timeline column (snapped), or null when the mouse is outside the timeline. */
export function mouseBeat(services: AppServices): Beat | null {
  const s = laneSession(services);
  const snap = services.editor.getState().snap;
  const host = hostOf(s);
  let info: { readonly beat: number } | null;
  try {
    // Recomputed from the pointer's y: the wheel may have scrolled time under a resting pointer.
    const h = host?.hoverInfo() ?? null;
    info = h === null || host === null ? null : { beat: host.axis.beatAtY(h.y) };
  } catch {
    info = null;
  }
  info ??= livePointer(s);
  if (info === null) return null;
  return snapBeatAt(info.beat, snap, false);
}

/** Anchor of a paste: the mouse beat (setting 'mouse', when over the timeline) or the playhead, snapped. */
export function pasteAnchor(services: AppServices, setting: 'mouse' | 'playhead'): Beat {
  const snap = services.editor.getState().snap;
  if (setting === 'mouse') {
    const b = mouseBeat(services);
    if (b !== null) return b;
  }
  return snapBeatAt(playheadBeat(services), snap, false);
}

/**
 * The paste mode and anchor of the settings. At the Guided level the default ghost paste becomes an immediate paste
 * (UX-045: beginners took the faint ghost waiting for Enter for a failed paste); a mode the user chose in Settings
 * is kept at every level, and Full keeps the ghost by default.
 */
export function pasteSettings(services: Pick<AppServices, 'settings' | 'editor'>): {
  readonly mode: 'ghost' | 'immediate';
  readonly anchor: 'mouse' | 'playhead';
} {
  try {
    const e = services.settings.value.editing;
    const chosen = services.settings.isDefault?.('editing.pasteMode') === false;
    const guided = services.editor.getState().level === 'guided';
    return { mode: guided && !chosen ? 'immediate' : e.pasteMode, anchor: e.pasteAnchor };
  } catch {
    return { mode: 'ghost', anchor: 'mouse' };
  }
}

/** This package's anchor and X offset choice for a paste ghost, or null for a ghost another package put up. */
function choiceFor(s: LaneSession, placing: PastePlacement): PasteGhostChoice | null {
  const c = s.pasteChoice;
  return c !== null && c.placement === placing ? c : null;
}

type PastePlacement = Extract<PlacementState, { kind: 'paste' }>;

/** Replaces the ghost's placement (M, Tab, brackets), keeping this package's anchor and X offset choice with it. */
function updateGhost(services: AppServices, prev: PastePlacement, next: PastePlacement): void {
  const s = laneSession(services);
  const choice = choiceFor(s, prev);
  s.pasteChoice = choice === null ? null : { ...choice, placement: next };
  services.editor.getState().setPlacement(next);
}

/**
 * Where the ghost's earliest item is now: the anchor plus the `[` / `]` shift in snap steps, never before 0. The
 * anchor is the one chosen when the ghost was put up (a paste special choice, else the "Paste anchor" setting); a
 * ghost handed over by another package follows the mouse.
 */
export function ghostAnchor(services: AppServices, placing: Extract<PlacementState, { kind: 'paste' }>): Beat {
  const choice = choiceFor(laneSession(services), placing);
  const base = pasteAnchor(services, choice?.anchor ?? 'mouse');
  const step = snapStepBeat(services.editor.getState().snap);
  const b = Beats.add(base, Beats.mulInt(step, placing.shift));
  return Beats.lt(b, Beats.ZERO) ? Beats.ZERO : b;
}

// ---------------------------------------------------------------------------------------------------------------
// Paste
// ---------------------------------------------------------------------------------------------------------------

export interface PasteRequest {
  readonly variant: PasteVariant;
  /** Force the ghost or an immediate paste (edit.pasteGhost / edit.pasteImmediate); default: the setting. */
  readonly force?: 'ghost' | 'immediate';
  /** Paste special choice (DIALOG.pasteSpecial). */
  readonly special?: NonNullable<PasteSpecialResult>;
  /** Paste right here (the right-clicked beat of the lane context menu): immediate, at this beat. */
  readonly at?: Beat;
  readonly command: string;
}

/** "The clipboard is empty: copy something first (Ctrl+C)" with the user's Copy shortcut. */
function nothingToPaste(services: AppServices): void {
  services.ui.toast(ref(m.toastNothingToPaste, { keys: commandKeysText(services, 'edit.copy') }), { kind: 'info' });
}

/** Lines a paste writes to: the target line, or the payload's source lines with "to original lines". */
export function pasteLines(
  services: Pick<AppServices, 'store'>,
  payload: ClipboardPayload,
  targetLine: LineId,
  mode: PasteMode,
): LineId[] {
  if (!mode.toOriginalLines) return [targetLine];
  const store = services.store;
  const slots = new Set<number>();
  for (const n of payload.notes) slots.add(n.lineSlot);
  for (const t of payload.tracks) slots.add(t.lineSlot);
  const out: LineId[] = [];
  for (const slot of slots) {
    const line = payload.lines[slot]?.sourceLine;
    if (line !== undefined && store?.doc.lines.has(line) === true) out.push(line);
  }
  return out;
}

/** Reads the clipboard and starts a paste (ghost or immediate). */
export async function startPaste(services: AppServices, req: PasteRequest): Promise<void> {
  const store = services.store;
  if (store === null) return;
  const payload = await services.clipboard.read();
  if (payload === null) {
    nothingToPaste(services);
    return;
  }
  const special = req.special;
  const targetLine = special?.targetLine ?? activeLineOf(services) ?? payload.lines[0]?.sourceLine ?? null;
  if (targetLine === null || (!store.doc.lines.has(targetLine) && special?.mode.toOriginalLines !== true)) {
    services.ui.toast(ref(m.reasonNoLine), { kind: 'info' });
    return;
  }
  const mode = special?.mode ?? modeFor(req.variant);
  const locked = lockedReason(services, { lines: pasteLines(services, payload, targetLine, mode) });
  if (locked !== null) {
    services.ui.toast(locked, { kind: 'warning' });
    return;
  }
  const xShift = special?.xShift ?? 0;
  const settings = pasteSettings(services);
  const anchorChoice = req.at ?? special?.anchor ?? settings.anchor;
  const immediate =
    req.force === 'immediate' ||
    typeof anchorChoice === 'object' ||
    mode.originalBeats ||
    (req.force !== 'ghost' && settings.mode === 'immediate');
  if (immediate) {
    const anchor = typeof anchorChoice === 'object' ? anchorChoice : pasteAnchor(services, anchorChoice);
    doPaste(services, { payload, anchor, targetLine, mode, xShift, command: req.command });
    return;
  }
  const e = services.editor.getState();
  const s = laneSession(services);
  const placement: PastePlacement = { kind: 'paste', payload, mode, targetLine, shift: 0 };
  s.pasteChoice = { placement, anchor: typeof anchorChoice === 'string' ? anchorChoice : settings.anchor, xShift };
  e.setPlacement(placement);
  pasteHints(services);
  s.redraw(['overlay']);
}

/** Gesture hints while the paste ghost is up. */
export function pasteHints(services: AppServices): void {
  gestureHints(services, [
    { chord: 'Enter', label: ref(m.ghostCommit) },
    { chord: 'Escape', label: ref(m.ghostCancel) },
    { chord: 'KeyM', label: ref(m.ghostMirror) },
    { chord: 'Tab', label: ref(m.ghostLine) },
    { chord: 'BracketRight', label: ref(m.ghostShift) },
  ]);
}

export interface DoPasteArgs {
  readonly payload: ClipboardPayload;
  readonly anchor: Beat;
  readonly targetLine: LineId;
  readonly mode: PasteMode;
  readonly xShift: number;
  readonly command: string;
}

/** One paste transaction; selects what was pasted and reports. Returns the pasted ids, or null on failure. */
export function doPaste(
  services: AppServices,
  a: DoPasteArgs,
): { readonly notes: NoteId[]; readonly keys: KeyId[] } | null {
  const store = services.store;
  if (store === null) return null;
  const locked = lockedReason(services, { lines: pasteLines(services, a.payload, a.targetLine, a.mode) });
  if (locked !== null) {
    services.ui.toast(locked, { kind: 'warning' });
    return null;
  }
  const rest = a.mode.addValues && !a.mode.asNewLayer ? unmatchedRest(services, a) : null;
  const items = itemsText(services, { notes: a.payload.notes.length, keys: payloadKeyCount(a.payload) });
  // Continuity of the target lines' keys before the paste (the store's document changes in place).
  const before = a.mode.addValues
    ? targetContinuity(store, pasteLines(services, a.payload, a.targetLine, a.mode))
    : null;
  const out = store.transact(
    { label: ref(a.command === 'edit.duplicate' ? m.txDuplicate : m.txPaste, { items }), command: a.command },
    (tx) => {
      const r = Core.clipboard.paste(tx, {
        payload: a.payload,
        anchor: a.anchor,
        targetLine: a.targetLine,
        mode: a.mode,
        xShift: a.xShift,
      });
      tx.setSelection({ notes: r.notes, keys: r.keys, segments: [] });
      return r;
    },
  );
  if (!out.ok) {
    // Add values with no matching keyframe at all: nothing was pasted, but the values can still go to a new layer.
    if (rest !== null) offerRest(services, rest, a);
    else services.ui.toast(out.reason, { kind: 'error' });
    return null;
  }
  const done = itemsText(services, { notes: out.value.notes.length, keys: out.value.keys.length });
  services.ui.toast(ref(a.command === 'edit.duplicate' ? m.toastDuplicated : m.toastPasted, { items: done }), {
    kind: 'success',
    undo: true,
    durationMs: 3000,
  });
  for (const n of out.notes) services.ui.toast(n, { kind: 'info' });
  if (before !== null) reportJumpsMade(services, before, out.value.keys);
  if (rest !== null) offerRest(services, rest, a);
  return out.value;
}

/** Continuity of every key of the given lines (see `reportJumpsMade`). */
export function targetContinuity(
  store: NonNullable<AppServices['store']>,
  lines: readonly LineId[],
): Map<KeyId, boolean> {
  const ids: KeyId[] = [];
  for (const line of lines) {
    for (const tracks of store.idx.tracksOfLine(line).values()) {
      for (const t of tracks) ids.push(...store.idx.keysByTrack(t));
    }
  }
  return continuityOf(store.doc, ids);
}

/**
 * "Made a jump at 3:0/1" with Undo and "Continue from previous" when an edit turned continuous keys into jumps
 * (UX-033: only a key after a Stay can, since every other one-sided edit keeps continuity). `before`: continuity
 * recorded before the transaction (`targetContinuity`).
 */
export function reportJumpsMade(
  services: AppServices,
  before: ReadonlyMap<KeyId, boolean>,
  keys: readonly KeyId[],
): void {
  const store = services.store;
  if (store === null || keys.length === 0) return;
  const made = jumpsMade(before, { doc: store.doc, idx: store.idx }, keys);
  const first = made[0] === undefined ? undefined : store.doc.keys.get(made[0]);
  if (first === undefined) return;
  services.ui.toast(ref(m.toastJumpMade, { count: made.length, at: beatText(services, first.t) }), {
    kind: 'info',
    undo: true,
    durationMs: 8000,
    action: { label: ref(m.actionContinueFromPrevious), command: 'segments.jump', args: { keys: made, keep: 'in' } },
  });
}

/** Toast with "Paste the rest as a new layer" for add-values keys without a target keyframe. */
function offerRest(services: AppServices, rest: ClipboardPayload, a: DoPasteArgs): void {
  const s = laneSession(services);
  const token = s.nextRestToken++;
  s.rests.set(token, { payload: rest, anchor: a.anchor, targetLine: a.targetLine, mirror: a.mode.mirror });
  services.ui.toast(ref(m.toastUnmatched, { count: payloadKeyCount(rest) }), {
    kind: 'warning',
    durationMs: 8000,
    action: { label: ref(m.actionPasteRest), command: 'edit.paste', args: { rest: token } },
  });
}

/**
 * Add-values keys that will find no target key at their beat (the core adds only onto existing keys), as a payload
 * of their own (no notes), or null when every value matches. Pasted later "as a new layer".
 */
export function unmatchedRest(services: AppServices, a: DoPasteArgs): ClipboardPayload | null {
  const store = services.store;
  if (store === null) return null;
  const base = a.mode.originalBeats ? a.payload.origin : a.anchor;
  const tracks: ClipTrackSlice[] = [];
  let count = 0;
  for (const slice of a.payload.tracks) {
    const line = a.mode.toOriginalLines ? (a.payload.lines[slice.lineSlot]?.sourceLine ?? a.targetLine) : a.targetLine;
    const track = store.idx.track(line, slice.prop, slice.layer);
    const beats = new Set<string>();
    if (track !== undefined) {
      for (const id of store.idx.keysByTrack(track)) {
        const k = store.doc.keys.get(id);
        if (k !== undefined) beats.add(Beats.key(k.t));
      }
    }
    const keys = slice.keys.filter((k) => {
      const t = Beats.add(base, k.t);
      return !Beats.lt(t, Beats.ZERO) && !beats.has(Beats.key(t));
    });
    if (keys.length === 0) continue;
    count += keys.length;
    tracks.push({ ...slice, keys });
  }
  if (count === 0) return null;
  return { ...a.payload, notes: [], tracks, wholeLines: [] };
}

/** "Paste the rest as a new layer" (toast action). */
export function pasteRest(services: AppServices, token: number): void {
  const s = laneSession(services);
  const rest = s.rests.get(token);
  if (rest === undefined) return;
  s.rests.delete(token);
  doPaste(services, {
    payload: rest.payload,
    anchor: rest.anchor,
    targetLine: rest.targetLine,
    mode: { ...NORMAL_MODE, asNewLayer: true, mirror: rest.mirror },
    xShift: 0,
    command: 'edit.paste',
  });
}

/**
 * Commits the ghost at its current position (Enter) or at a clicked beat. On a locked target line the ghost stays up
 * (Tab picks another line) and a toast says why.
 */
export function commitGhost(services: AppServices, at?: Beat): boolean {
  const e = services.editor.getState();
  const placing = e.placement;
  if (placing?.kind !== 'paste') return false;
  const locked = lockedReason(services, {
    lines: pasteLines(services, placing.payload, placing.targetLine, placing.mode),
  });
  if (locked !== null) {
    services.ui.toast(locked, { kind: 'warning' });
    return false;
  }
  const s = laneSession(services);
  const anchor = at ?? ghostAnchor(services, placing);
  const xShift = choiceFor(s, placing)?.xShift ?? 0;
  e.setPlacement(null);
  s.pasteChoice = null;
  gestureHints(services, null);
  s.redraw(['overlay']);
  return (
    doPaste(services, {
      payload: placing.payload,
      anchor,
      targetLine: placing.targetLine,
      mode: placing.mode,
      xShift,
      command: 'edit.paste',
    }) !== null
  );
}

export function cancelGhost(services: AppServices): void {
  const e = services.editor.getState();
  if (e.placement?.kind !== 'paste') return;
  e.setPlacement(null);
  gestureHints(services, null);
  const s = laneSession(services);
  s.pasteChoice = null;
  s.redraw(['overlay']);
}

/** M while the ghost is up. */
export function toggleGhostMirror(services: AppServices): void {
  const e = services.editor.getState();
  const p = e.placement;
  if (p?.kind !== 'paste') return;
  updateGhost(services, p, { ...p, mode: { ...p.mode, mirror: !p.mode.mirror } });
  laneSession(services).redraw(['overlay']);
}

/** [ / ] while the ghost is up. */
export function shiftGhost(services: AppServices, steps: number): void {
  const e = services.editor.getState();
  const p = e.placement;
  if (p?.kind !== 'paste') return;
  updateGhost(services, p, { ...p, shift: p.shift + steps });
  laneSession(services).redraw(['overlay']);
}

/** Tab / Shift+Tab while the ghost is up: next / previous line in export order. */
export function cycleGhostLine(services: AppServices, dir: 1 | -1): void {
  const store = services.store;
  const e = services.editor.getState();
  const p = e.placement;
  if (store === null || p?.kind !== 'paste') return;
  const order = store.doc.lineOrder;
  if (order.length === 0) return;
  const i = order.indexOf(p.targetLine);
  const next = order[(i + dir + order.length) % order.length] ?? p.targetLine;
  updateGhost(services, p, { ...p, targetLine: next });
  // The lane follows the target so the ghost stays visible.
  e.setActiveLine(next);
  laneSession(services).redraw(['overlay']);
}

/** Ghost of the notes landing on `laneLine`, with its label, or null when no paste ghost is up. */
export function pasteOverlay(
  services: AppServices,
  laneLine: LineId | null,
): { readonly ghost: PasteGhost; readonly label: string | null } | null {
  const store = services.store;
  const p = services.editor.getState().placement;
  if (store === null || laneLine === null || p?.kind !== 'paste') return null;
  const anchor = ghostAnchor(services, p);
  const ghost = pasteGhostNotes({
    payload: p.payload,
    anchor: Beats.toF64(anchor),
    mode: p.mode,
    xShift: choiceFor(laneSession(services), p)?.xShift ?? 0,
    laneLine,
    targetLine: p.targetLine,
  });
  const lineName = lineLabel(services, store.doc, store.idx, p.targetLine);
  const label = isLockedLine(services, p.targetLine)
    ? services.i18n.t(m.ghostLabelLocked, { line: lineName })
    : p.payload.notes.length > 0
      ? services.i18n.t(m.ghostLabel, { count: ghost.total, line: lineName, mirror: p.mode.mirror ? 'yes' : 'no' })
      : services.i18n.t(m.ghostLabelKeys, {
          items: itemsText(services, { keys: payloadKeyCount(p.payload) }),
          line: lineName,
        });
  return { ghost, label };
}

// ---------------------------------------------------------------------------------------------------------------
// Duplicate and paste special
// ---------------------------------------------------------------------------------------------------------------

/** Ctrl+D: the selection pasted right after its own span, on its own lines. */
export function duplicateSelection(services: AppServices): void {
  const store = services.store;
  if (store === null) return;
  const sel = selectedItems(services);
  if (sel.notes.length + sel.keys.length + sel.segments.length === 0) {
    services.ui.toast(ref(m.reasonNoSelection), { kind: 'info' });
    return;
  }
  const payload = Core.clipboard.copy(
    { doc: store.doc, idx: store.idx, tempo: tempoOfStore(store) },
    { notes: sel.notes, keys: sel.keys, segments: sel.segments },
  );
  if (payload === null) return;
  // The copy starts one snap step after the selection's last item, so a phrase on the grid repeats with its own
  // period (notes at 0 .. 3:3/4 with 1/4 snap repeat at 4 .. 7:3/4).
  const step = snapStepBeat(services.editor.getState().snap);
  const gap = Beats.add(payload.span, step);
  const targetLine = activeLineOf(services) ?? payload.lines[0]?.sourceLine ?? null;
  if (targetLine === null) return;
  doPaste(services, {
    payload,
    anchor: Beats.add(payload.origin, gap),
    targetLine,
    mode: { ...NORMAL_MODE, toOriginalLines: true },
    xShift: 0,
    command: 'edit.duplicate',
  });
}

/** Opens paste special (W2-11's dialog chooses; this package pastes). */
export async function pasteSpecial(services: AppServices): Promise<void> {
  const payload = await services.clipboard.read();
  if (payload === null) {
    nothingToPaste(services);
    return;
  }
  const targetLine = activeLineOf(services) ?? payload.lines[0]?.sourceLine ?? null;
  if (targetLine === null) {
    services.ui.toast(ref(m.reasonNoLine), { kind: 'info' });
    return;
  }
  const result = await services.ui.openDialog(DIALOG.pasteSpecial, { payload, targetLine });
  if (result === null) return;
  await startPaste(services, { variant: 'normal', special: result, command: 'edit.paste' });
}

// ---------------------------------------------------------------------------------------------------------------
// Arguments of edit.paste (keymap, palette, toasts, W2-11's edit.pasteSpecial)
// ---------------------------------------------------------------------------------------------------------------

export type PasteArgs =
  | { readonly kind: 'paste'; readonly variant: PasteVariant }
  | { readonly kind: 'specialDialog' }
  | { readonly kind: 'special'; readonly result: NonNullable<PasteSpecialResult> }
  | { readonly kind: 'rest'; readonly token: number };

function obj(v: unknown): Readonly<Record<string, unknown>> | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function beatArg(v: unknown): Beat | null {
  const o = obj(v);
  if (o === null) return null;
  const { w, n, d } = o;
  if (typeof w !== 'number' || typeof n !== 'number' || typeof d !== 'number') return null;
  try {
    return Beats.make(w, n, d);
  } catch {
    return null;
  }
}

function modeArg(v: unknown): PasteMode | null {
  const o = obj(v);
  if (o === null) return null;
  const bool = (k: string): boolean => o[k] === true;
  const repeat = typeof o.repeat === 'number' && Number.isSafeInteger(o.repeat) && o.repeat >= 1 ? o.repeat : 1;
  const every = o.every === undefined ? null : beatArg(o.every);
  return {
    mirror: bool('mirror'),
    addValues: bool('addValues'),
    asNewLayer: bool('asNewLayer'),
    originalBeats: bool('originalBeats'),
    toOriginalLines: bool('toOriginalLines'),
    repeat,
    ...(every === null ? {} : { every }),
  };
}

/**
 * Parses the JSON arguments of `edit.paste`: a variant string (palette choice) or `{ variant }` (Ctrl+B, Alt+V),
 * `{ special: true }` (open paste special), `{ special: PasteSpecialResult }` (a paste special choice made elsewhere,
 * e.g. W2-11's edit.pasteSpecial; anchor 'mouse' | 'playhead' | BeatArg), `{ rest: token }` (toast action).
 */
export function parsePasteArgs(args: unknown): PasteArgs {
  if (args === 'mirror' || args === 'addValues' || args === 'normal') return { kind: 'paste', variant: args };
  if (args === 'special') return { kind: 'specialDialog' };
  const o = obj(args);
  if (o === null) return { kind: 'paste', variant: 'normal' };
  if (typeof o.rest === 'number') return { kind: 'rest', token: o.rest };
  if (o.special === true) return { kind: 'specialDialog' };
  const sp = obj(o.special);
  if (sp !== null) {
    const mode = modeArg(sp.mode);
    const anchor = sp.anchor === 'mouse' || sp.anchor === 'playhead' ? sp.anchor : beatArg(sp.anchor);
    const targetLine = typeof sp.targetLine === 'number' ? (sp.targetLine as LineId) : null;
    const xShift = typeof sp.xShift === 'number' && Number.isFinite(sp.xShift) ? sp.xShift : 0;
    if (mode !== null && anchor !== null && targetLine !== null) {
      return { kind: 'special', result: { mode, anchor, targetLine, xShift } };
    }
  }
  const v = o.variant;
  return { kind: 'paste', variant: v === 'mirror' || v === 'addValues' ? v : 'normal' };
}

/** JSON form of a paste special result (for `registry.runAny('edit.paste', ...)`). */
export function specialArgs(r: NonNullable<PasteSpecialResult>): Json {
  const anchor: Json = typeof r.anchor === 'string' ? r.anchor : { w: r.anchor.w, n: r.anchor.n, d: r.anchor.d };
  const mode: Record<string, Json> = {
    mirror: r.mode.mirror,
    addValues: r.mode.addValues,
    asNewLayer: r.mode.asNewLayer,
    originalBeats: r.mode.originalBeats,
    toOriginalLines: r.mode.toOriginalLines,
    repeat: r.mode.repeat,
  };
  if (r.mode.every !== undefined) mode.every = { w: r.mode.every.w, n: r.mode.every.n, d: r.mode.every.d };
  return { special: { mode, anchor, targetLine: r.targetLine, xShift: r.xShift } };
}
